/**
 * ComplianceGuardCard — opt-in local watering-ordinance enforcement.
 *
 * Single component per DESIGN_PRINCIPLES §47 — every place compliance
 * settings render reads from `/api/irrigation/schedules/compliance-programs`
 * (the canonical registry). Adding a new municipality to
 * `services/compliance_guard.py` lights up here automatically.
 *
 * The "Custom" option lets users in cities that aren't in the registry
 * enter their own HH:MM blackout window (e.g. their HOA's ordinance, or
 * a personal "never water during work-from-home Zooms" preference).
 * Supports cross-midnight windows (e.g. 23:00 → 05:00 to forbid overnight).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { ShieldCheck, ExternalLink, Save } from 'lucide-react';

const ENDPOINTS = {
  programs: `${API_URL}/api/irrigation/schedules/compliance-programs`,
  current:  `${API_URL}/api/irrigation/schedules/compliance-program/current`,
  set:      `${API_URL}/api/irrigation/schedules/compliance-program`,
};

const CUSTOM_SLUG = 'custom';

// "HH:MM" → integer hour (rounded down). Returns null if invalid.
const parseHourFromHHMM = (s) => {
  if (typeof s !== 'string') return null;
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  if (Number.isNaN(h) || h < 0 || h > 23) return null;
  return h;
};

const hourToHHMM = (h) => `${String(h).padStart(2, '0')}:00`;

export const ComplianceGuardCard = () => {
  const [programs, setPrograms] = useState([]);
  const [current, setCurrent] = useState(null);   // {slug, program, custom_window}
  const [saving, setSaving] = useState(false);

  // Custom window form state — only used when slug === 'custom'.
  const [customStart, setCustomStart] = useState('10:00');
  const [customEnd, setCustomEnd] = useState('19:00');

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        axios.get(ENDPOINTS.programs, { withCredentials: true }),
        axios.get(ENDPOINTS.current,  { withCredentials: true }),
      ]);
      setPrograms(p.data?.programs || []);
      const cur = c.data || {};
      setCurrent(cur);
      // Seed form from saved custom window if present.
      const cw = cur.custom_window;
      if (cw) {
        if (typeof cw.forbid_start_hour === 'number') setCustomStart(hourToHHMM(cw.forbid_start_hour));
        if (typeof cw.forbid_end_hour === 'number') setCustomEnd(hourToHHMM(cw.forbid_end_hour));
      }
    } catch (e) {
      // Card stays hidden on auth/network failure — don't pollute the profile page.
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const persist = async (slug, customWindow = undefined) => {
    setSaving(true);
    try {
      const body = { slug };
      if (slug === CUSTOM_SLUG && customWindow) body.custom_window = customWindow;
      const res = await axios.put(ENDPOINTS.set, body, { withCredentials: true });
      setCurrent({
        slug: res.data?.slug,
        program: res.data?.program,
        custom_window: customWindow || null,
      });
      toast.success(slug
        ? `Compliance Guard ON — ${res.data?.program?.region || res.data?.program?.label || 'Custom'}`
        : 'Compliance Guard disabled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save compliance setting');
    } finally {
      setSaving(false);
    }
  };

  const choose = async (slug) => {
    // Selecting "custom" from the dropdown shows the inputs but doesn't
    // persist yet — user has to set times + click Save first.
    if (slug === CUSTOM_SLUG) {
      setCurrent((prev) => ({ ...(prev || {}), slug: CUSTOM_SLUG, program: null }));
      return;
    }
    await persist(slug || null);
  };

  const saveCustom = async () => {
    const fs = parseHourFromHHMM(customStart);
    const fe = parseHourFromHHMM(customEnd);
    if (fs === null || fe === null) {
      toast.error('Enter both times as HH:MM (24h).');
      return;
    }
    if (fs === fe) {
        toast.error('Start and end can not match. Pick at least a 1-hour window.');
      return;
    }
    await persist(CUSTOM_SLUG, {
      forbid_start_hour: fs,
      forbid_end_hour: fe,
      label: `Custom: ${hourToHHMM(fs)}–${hourToHHMM(fe)}`,
    });
  };

  if (!programs.length) return null;
  const activeSlug = current?.slug || '';
  const activeProgram = current?.program;
  const isCustom = activeSlug === CUSTOM_SLUG;
  const fsParsed = parseHourFromHHMM(customStart);
  const feParsed = parseHourFromHHMM(customEnd);
  const crossMidnight = fsParsed !== null && feParsed !== null && fsParsed > feParsed;

  return (
    <div
      data-testid="compliance-guard-card"
      className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-600/40 flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1">
          <div className="text-base font-semibold text-slate-100">Compliance Guard</div>
          <div className="text-xs text-slate-400 mt-0.5 leading-snug">
            Block schedules and auto-fires that violate your local watering ordinance.
            TurfPulse refuses to dispatch Rachio during forbidden hours, even if the engine
            decides the lawn is dry.
          </div>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-2">
          Your program
        </label>
        <select
          data-testid="compliance-guard-select"
          value={activeSlug}
          disabled={saving}
          onChange={(e) => choose(e.target.value || null)}
          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
        >
          <option value="">— No restrictions (opt out)</option>
          {programs.map((p) => (
            <option key={p.slug} value={p.slug}>{p.label}</option>
          ))}
          <option value={CUSTOM_SLUG}>Custom — enter my own hours</option>
        </select>
      </div>

      {isCustom && (
        <div
          data-testid="compliance-guard-custom-form"
          className="bg-slate-950/60 border border-slate-800 rounded-lg p-3 space-y-3"
        >
          <div className="text-xs text-slate-300 leading-snug">
            Enter the hours irrigation should be <b className="text-amber-300">blocked</b>.
            Times are 24-hour (00:00–23:59) in your local timezone.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">
                Start (HH:MM)
              </label>
              <input
                type="time"
                data-testid="compliance-guard-custom-start"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-100"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">
                End (HH:MM)
              </label>
              <input
                type="time"
                data-testid="compliance-guard-custom-end"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-100"
              />
            </div>
          </div>
          {crossMidnight && (
            <div className="text-[11px] text-amber-300 leading-snug">
              Cross-midnight window detected. Watering will be blocked from{' '}
              <b>{customStart}</b> through midnight and again from{' '}
              midnight until <b>{customEnd}</b>.
            </div>
          )}
          <button
            data-testid="compliance-guard-custom-save"
            disabled={saving}
            onClick={saveCustom}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold px-3 py-1.5 rounded-lg"
          >
            <Save className="w-4 h-4" />
            Save custom window
          </button>
        </div>
      )}

      {activeProgram && !isCustom && (
        <div
          data-testid="compliance-guard-active"
          className="bg-emerald-950/30 border border-emerald-900/60 rounded-lg p-3 text-xs text-emerald-200"
        >
          <div className="font-semibold mb-1">
            ✓ {activeProgram.region}
          </div>
          <div className="leading-relaxed text-emerald-300/90">
            Forbidden window: <span className="font-mono">{String(activeProgram.forbid_start_hour).padStart(2, '0')}:00</span>
            {' — '}
            <span className="font-mono">{String(activeProgram.forbid_end_hour).padStart(2, '0')}:00</span>
            {' '}{activeProgram.timezone}.
          </div>
          {activeProgram.citation_url && (
            <a
              href={activeProgram.citation_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-emerald-400 hover:underline"
              data-testid="compliance-guard-citation"
            >
              {activeProgram.citation}<ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}

      {activeProgram && isCustom && (
        <div
          data-testid="compliance-guard-custom-active"
          className="bg-emerald-950/30 border border-emerald-900/60 rounded-lg p-3 text-xs text-emerald-200"
        >
          <div className="font-semibold mb-1">✓ Custom window active</div>
          <div className="leading-relaxed text-emerald-300/90">
            Forbidden window:{' '}
            <span className="font-mono">{String(activeProgram.forbid_start_hour).padStart(2, '0')}:00</span>
            {' — '}
            <span className="font-mono">{String(activeProgram.forbid_end_hour).padStart(2, '0')}:00</span>
            {' '}{activeProgram.timezone}.
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceGuardCard;
